import React, { useEffect, useState } from "react";

/**
 * Selector del medio de cobro de una venta.
 * Por defecto "Efectivo / Transferencia" (sin comisión). Si la empresa cargó
 * procesadoras (Bancard, Dinelco, Pix...) se pueden elegir con su medio
 * (crédito, débito, QR...). La comisión se calcula en el servidor; acá solo se
 * muestra una vista previa.
 *
 * value: { procesadora_id: "", metodo_cobro: "" }  ("" = efectivo/transferencia)
 */
export function useProcesadoras(API, token, logoTipo) {
  const [lista, setLista] = useState([]);
  useEffect(() => {
    if (!token || !logoTipo) { setLista([]); return; }
    let vivo = true;
    fetch(`${API}/admin/procesadoras-cobro?logo_tipo=${encodeURIComponent(logoTipo)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (vivo) setLista(Array.isArray(d) ? d.filter(p => (p.metodos || []).length > 0) : []); })
      .catch(() => { if (vivo) setLista([]); });
    return () => { vivo = false; };
  }, [API, token, logoTipo]);
  return lista;
}

export const SIN_MEDIO = { procesadora_id: "", metodo_cobro: "" };

export function comisionPreview(procesadoras, value, monto) {
  if (!value?.procesadora_id) return { pct: 0, comision: 0 };
  const p = procesadoras.find(x => x.id === value.procesadora_id);
  const m = p?.metodos?.find(x => x.id === value.metodo_cobro);
  const pct = Number(m?.pct || 0);
  return { pct, comision: Math.round(Number(monto || 0) * pct) / 100 };
}

export default function MedioCobroSelector({ procesadoras, value, onChange, monto = 0, fmt = (n) => String(n), theme = "dark" }) {
  if (!procesadoras || procesadoras.length === 0) return null;
  const light = theme === "light";
  const base = light ? "border-gray-300 text-gray-700 bg-white hover:bg-gray-50" : "border-white/10 text-slate-300 bg-white/5 hover:bg-white/10";
  const sel = light ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-emerald-400 bg-emerald-500/20 text-emerald-200";
  const lbl = light ? "text-gray-500" : "text-slate-400";
  const efectivo = !value?.procesadora_id;
  const { pct, comision } = comisionPreview(procesadoras, value, monto);
  return (
    <div className="space-y-2">
      <label className={`text-xs block ${lbl}`}>Medio de cobro</label>
      <button type="button" onClick={() => onChange(SIN_MEDIO)}
        className={`w-full text-left px-3 py-2 rounded-lg border text-sm font-medium ${efectivo ? sel : base}`}>
        Efectivo / Transferencia <span className="opacity-60 font-normal">· sin comisión</span>
      </button>
      {procesadoras.map(p => (
        <div key={p.id}>
          <p className={`text-[11px] uppercase tracking-wide mb-1 ${lbl}`}>{p.nombre}</p>
          <div className="flex flex-wrap gap-1.5">
            {(p.metodos || []).map(m => {
              const activo = value?.procesadora_id === p.id && value?.metodo_cobro === m.id;
              return (
                <button key={m.id} type="button" onClick={() => onChange({ procesadora_id: p.id, metodo_cobro: m.id })}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${activo ? sel : base}`}>
                  {m.nombre} <span className="opacity-60">{Number(m.pct || 0)}%</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {!efectivo && (
        <p className={`text-[11px] ${light ? "text-amber-600" : "text-amber-300"}`}>
          Comisión {pct}%: {fmt(comision)} — a la cuenta entran {fmt(Math.max(0, Number(monto || 0) - comision))}.
        </p>
      )}
    </div>
  );
}
